/**
 * H.A.6 — Wizard compartido Producto personalizado (Stock / Ventas / Presupuesto).
 * Thin wrapper sobre ConfiguradorProductoFormBody (mode=create).
 *
 * persistMode:
 * - "immediate" (default): POST createPersonalizado (venta / stock).
 * - "none": no crea Producto; devuelve snapshot local (presupuesto diferido).
 */
import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  APIError,
  ProductoPersonalizadoConflict,
  ProductoPersonalizadoCreated,
  productosAPI,
  proveedoresAPI,
} from "@/lib/api";
import { dataRefresh } from "@/lib/dataRefresh";
import { toMoneyNumber } from "@/lib/parseArgentineDecimal";
import { ProveedoresABM } from "../Proveedores/ProveedoresABM";
import {
  ConfiguradorProductoFormBody,
  inputClass,
  labelClass,
  useConfiguradorProductoForm,
} from "./ConfiguradorProductoFormBody";
import {
  formatMoney,
  DESCRIPCION_FIELD_SEPARATOR,
  DESCRIPCION_MAX_LEN,
  KEY_OBS,
  KEY_STOCK,
} from "./configuradorProductoShared";

const FONDOS_UNIFICAR = [
  "rounded-md bg-muted/40 p-3",
  "rounded-md bg-muted/70 p-3",
  "rounded-md bg-secondary p-3",
  "rounded-md bg-accent/40 p-3",
];

const FLECHA_UNIFICAR =
  "bg-background shadow-xs hover:border-foreground/40 hover:bg-muted hover:text-foreground hover:shadow-md dark:bg-background dark:hover:border-foreground/45 dark:hover:bg-muted dark:hover:text-foreground";

export type ConfiguradorProductoExistente = ProductoPersonalizadoConflict["existente"];

/** Snapshot local para presupuesto (sin Producto persistido). */
export type PersonalizadoQuoteSnapshot = {
  selecciones: Record<string, unknown>;
  seleccionesUnificar?: Record<string, unknown> | Record<string, unknown>[];
  nombre: string;
  precio: number;
  codigoProvisional: string;
  proveedorId?: number | null;
  precioBase?: number;
};

export type ConfiguradorPersistMode = "immediate" | "none";

export interface ConfiguradorProductoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Tras 201 Created (solo persistMode immediate). */
  onCreated?: (result: ProductoPersonalizadoCreated) => void;
  /**
   * Abrir/navegar al producto existente (409).
   * Si no se provee: toast con código y se cierra el modal de conflicto.
   * En Nueva venta (H.A.7): usar el existente en el carrito.
   */
  onOpenExistente?: (existente: ConfiguradorProductoExistente) => void;
  /** Label del botón de acción del modal 409. Default: según contexto. */
  existenteActionLabel?: string;
  /**
   * "immediate" = createPersonalizado (default).
   * "none" = presupuesto: no POST; emite onSnapshot.
   */
  persistMode?: ConfiguradorPersistMode;
  /** Callback modo quote (persistMode none). */
  onSnapshot?: (snapshot: PersonalizadoQuoteSnapshot) => void;
  /** Título del dialog. */
  title?: string;
  /** Descripción del dialog. */
  description?: string;
  /** Label del botón de confirmación. */
  confirmLabel?: string;
  /**
   * Mostrar «Nuevo proveedor» y abrir el modal de alta.
   * Elevado / ABM Proveedores / flag Ventas.crear_proveedor.
   */
  canCreateProveedor?: boolean;
}

function isApiError(e: unknown): e is APIError {
  return (
    e !== null &&
    typeof e === "object" &&
    "error" in e &&
    Boolean((e as APIError).error)
  );
}

function parseConflict(err: APIError): ProductoPersonalizadoConflict | null {
  const data = err.data;
  if (!data || typeof data !== "object") return null;
  const code = (data.code as string) || err.code;
  if (code !== "config_hash_conflict" && err.status !== 409) return null;
  const existenteRaw = data.existente as Record<string, unknown> | undefined;
  if (!existenteRaw || typeof existenteRaw !== "object") return null;
  return {
    code: "config_hash_conflict",
    detail: String(data.detail || err.message || "La configuración ya existe"),
    configHash: (data.configHash as string) || (data.config_hash as string),
    existente: {
      id: Number(existenteRaw.id),
      codigo: String(existenteRaw.codigo || ""),
      nombre: String(existenteRaw.nombre || ""),
      precioBase:
        (existenteRaw.precioBase as string | null | undefined) ??
        (existenteRaw.precio_base as string | null | undefined) ??
        null,
      precioFinal:
        (existenteRaw.precioFinal as string | null | undefined) ??
        (existenteRaw.precio_final as string | null | undefined) ??
        null,
      esPersonalizado: Boolean(
        existenteRaw.esPersonalizado ?? existenteRaw.es_personalizado,
      ),
      configHash:
        (existenteRaw.configHash as string | null | undefined) ??
        (existenteRaw.config_hash as string | null | undefined) ??
        null,
      proveedorId:
        (existenteRaw.proveedorId as number | null | undefined) ??
        (existenteRaw.proveedor_id as number | null | undefined) ??
        null,
    },
  };
}

export function ConfiguradorProductoDialog({
  open,
  onOpenChange,
  onCreated,
  onOpenExistente,
  existenteActionLabel,
  persistMode = "immediate",
  onSnapshot,
  title,
  description,
  confirmLabel,
  canCreateProveedor = false,
}: ConfiguradorProductoDialogProps) {
  const isQuote = persistMode === "none";
  const [submitting, setSubmitting] = useState(false);
  const [conflict, setConflict] = useState<ProductoPersonalizadoConflict | null>(
    null,
  );

  const [cantidad, setCantidad] = useState<1 | 2 | 3 | 4>(1);
  const [slide, setSlide] = useState(0);
  const [avisoProveedor, setAvisoProveedor] = useState(false);
  const form = useConfiguradorProductoForm({
    open,
    mode: "create",
    includeStock: !isQuote,
    canCreateProveedor,
  });
  const form2 = useConfiguradorProductoForm({
    open: open && cantidad >= 2,
    mode: "create",
    includeStock: false,
    canCreateProveedor,
  });
  const form3 = useConfiguradorProductoForm({
    open: open && cantidad >= 3,
    mode: "create",
    includeStock: false,
    canCreateProveedor,
  });
  const form4 = useConfiguradorProductoForm({
    open: open && cantidad >= 4,
    mode: "create",
    includeStock: false,
    canCreateProveedor,
  });
  const forms = [form, form2, form3, form4];
  useEffect(() => {
    if (!open) {
      setCantidad(1);
      setSlide(0);
      setAvisoProveedor(false);
    }
  }, [open]);
  const {
    loadingCatalog,
    evaluating,
    arbol,
    fabricaValue,
    isOtras,
    proveedorId,
    resetForm,
    validateClient,
    setClientErrors,
    buildSeleccionesPayload,
    descripcionPreview,
    precioPreview,
    precioRaw,
    observacion,
    setComercial,
  } = form;

  const parteTecnica = (
    item: (typeof forms)[number],
  ) => {
    const full = (item.descripcionPreview || "").trim();
    const obs = item.observacion.trim();
    const sufijo = `${DESCRIPCION_FIELD_SEPARATOR}${obs}`;
    if (obs && full.endsWith(sufijo)) return full.slice(0, -sufijo.length).trim();
    return full;
  };

  const proveedorDistinto = (item: (typeof forms)[number]) => {
    if (!fabricaValue || !item.fabricaValue) return false;
    if (item.fabricaValue !== fabricaValue) return true;
    if (!isOtras && !item.isOtras) return false;
    if (!proveedorId || !item.proveedorId) return false;
    return proveedorId !== item.proveedorId;
  };

  const distintos = forms.slice(1, cantidad).some(proveedorDistinto);
  const formVisible = forms[Math.min(slide, Math.max(cantidad - 1, 0))] ?? form;

  useEffect(() => {
    if (distintos) setAvisoProveedor(true);
  }, [distintos]);

  const elegirCantidad = (n: 1 | 2 | 3 | 4) => {
    setCantidad(n);
    setSlide((actual) => Math.min(actual, Math.max(n - 1, 0)));
  };

  const leerUnificar = ():
    | { ok: true; selecciones?: Record<string, unknown>[] }
    | { ok: false } => {
    if (cantidad < 2) return { ok: true };
    for (let i = 1; i < cantidad; i += 1) {
      const item = forms[i];
      const errorsItem = item.validateClient({ soloTecnico: true });
      item.setClientErrors(errorsItem);
      if (errorsItem.length) {
        setSlide(i);
        toast.error(errorsItem[0]);
        return { ok: false };
      }
    }
    if (forms.slice(1, cantidad).some(proveedorDistinto)) {
      setAvisoProveedor(true);
      return { ok: false };
    }
    return {
      ok: true,
      selecciones: forms.slice(1, cantidad).map((item) => item.buildSeleccionesPayload()),
    };
  };

  const nombreUnificado = () => {
    if (cantidad < 2) {
      return ((descripcionPreview || "").trim() || "Producto personalizado").slice(
        0,
        DESCRIPCION_MAX_LEN,
      );
    }
    const partes = forms
      .slice(0, cantidad)
      .map(parteTecnica)
      .filter(Boolean);
    const obs = observacion.trim();
    let joined = partes.join(" + ") || "Producto personalizado";
    if (obs) joined = `${joined}${DESCRIPCION_FIELD_SEPARATOR}${obs}`;
    return joined.slice(0, DESCRIPCION_MAX_LEN);
  };

  const handleSubmitQuote = () => {
    const errors = validateClient();
    setClientErrors(errors);
    if (errors.length) {
      toast.error(errors[0]);
      return;
    }

    const unificado = leerUnificar();
    if (!unificado.ok) return;

    const seleccionesPayload = buildSeleccionesPayload();
    // Presupuesto: no impactar stock.
    seleccionesPayload[KEY_STOCK] = "0";
    delete seleccionesPayload.sucursal_deposito;

    const precioBase = toMoneyNumber(precioRaw) ?? 0;
    const precioFinal =
      toMoneyNumber(precioPreview?.precioFinalConIva ?? precioBase) ?? 0;
    const precio =
      Number.isFinite(precioFinal) && precioFinal > 0
        ? precioFinal
        : Number.isFinite(precioBase)
          ? precioBase
          : 0;

    if (!(precio > 0)) {
      toast.error("Indicá un precio válido");
      return;
    }

    const nombre = nombreUnificado();
    // Código provisional legible; el BE lo normaliza a CP-{hash[:12]} al guardar.
    const codigoProvisional = `CP-BORR-${Date.now().toString(36).toUpperCase()}`;

    onSnapshot?.({
      selecciones: seleccionesPayload,
      ...(unificado.selecciones?.length
        ? { seleccionesUnificar: unificado.selecciones }
        : {}),
      nombre,
      precio,
      precioBase: Number.isFinite(precioBase) ? precioBase : precio,
      codigoProvisional,
      proveedorId: isOtras && proveedorId ? proveedorId : null,
    });
    toast.success("Ítem personalizado agregado al presupuesto");
    onOpenChange(false);
    resetForm();
  };

  const handleSubmit = async () => {
    if (isQuote) {
      handleSubmitQuote();
      return;
    }

    const errors = validateClient();
    setClientErrors(errors);
    if (errors.length) {
      toast.error(errors[0]);
      return;
    }
    const unificado = leerUnificar();
    if (!unificado.ok) return;

    setSubmitting(true);
    try {
      const seleccionesPayload = buildSeleccionesPayload();
      const result = await productosAPI.createPersonalizado({
        selecciones: seleccionesPayload,
        ...(unificado.selecciones?.length
          ? { seleccionesUnificar: unificado.selecciones }
          : {}),
        proveedorId: isOtras && proveedorId ? proveedorId : undefined,
      });
      toast.success(
        `Producto ${result.producto?.codigo || ""} creado`.trim(),
      );
      dataRefresh.emit("stock");
      onCreated?.(result);
      onOpenChange(false);
      resetForm();
    } catch (e: unknown) {
      if (isApiError(e)) {
        const conf = parseConflict(e);
        if (conf) {
          setConflict(conf);
          return;
        }
        const msg =
          e.message ||
          (typeof e.details === "string" ? e.details : null) ||
          "No se pudo crear el producto personalizado";
        toast.error(msg);
        if (Array.isArray((e.data as { errors?: unknown })?.errors)) {
          const list = (e.data as { errors: Array<{ detail?: string }> }).errors
            .map((x) => x.detail)
            .filter(Boolean) as string[];
          if (list.length) setClientErrors(list);
        }
        return;
      }
      toast.error("No se pudo crear el producto personalizado");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenExistente = () => {
    if (!conflict) return;
    const existente = conflict.existente;
    if (onOpenExistente) {
      onOpenExistente(existente);
    } else {
      toast.info(
        `Producto existente: ${existente.codigo} — ${existente.nombre}`,
      );
    }
    setConflict(null);
    onOpenChange(false);
    resetForm();
  };

  const busy = loadingCatalog || submitting;
  // No incluir `evaluating` en formDisabled: deshabilitar todos los inputs
  // durante evaluar + FocusScope de Radix manda el Tab al primer campo.
  const formDisabled = busy;

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!v && submitting) return;
          onOpenChange(v);
          if (!v) resetForm();
        }}
      >
        <DialogContent className="sm:max-w-[720px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {title ||
                (isQuote
                  ? "Ítem personalizado (presupuesto)"
                  : "Producto personalizado")}
            </DialogTitle>
            <DialogDescription>
              {description ||
                (isQuote
                  ? "La configuración se guarda en el presupuesto. El producto se crea recién al generar la venta."
                  : "Configurá un ítem FAVRE u otra fábrica. El backend valida, calcula precio final e identidad técnica.")}
            </DialogDescription>
          </DialogHeader>

          {cantidad === 1 ? (
            <ConfiguradorProductoFormBody
              form={form}
              formDisabled={formDisabled}
              parte="tecnico"
              hideErrors
            />
          ) : (
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <div className="sticky top-14 z-10 self-start">
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className={FLECHA_UNIFICAR}
                    aria-label="Anterior"
                    disabled={formDisabled || slide === 0}
                    onClick={() => setSlide((actual) => Math.max(0, actual - 1))}
                  >
                    <ChevronLeft />
                  </Button>
                </div>
                <div className={`min-w-0 flex-1 ${FONDOS_UNIFICAR[slide] ?? FONDOS_UNIFICAR[0]}`}>
                  <ConfiguradorProductoFormBody
                    form={forms[slide]}
                    formDisabled={formDisabled}
                    parte="tecnico"
                    idPrefix={slide === 0 ? "" : `unificar-${slide}-`}
                    hideErrors={slide === 0}
                  />
                </div>
                <div className="sticky top-14 z-10 self-start">
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className={FLECHA_UNIFICAR}
                    aria-label="Siguiente"
                    disabled={formDisabled || slide >= cantidad - 1}
                    onClick={() =>
                      setSlide((actual) => Math.min(cantidad - 1, actual + 1))
                    }
                  >
                    <ChevronRight />
                  </Button>
                </div>
              </div>
              <div className="flex items-center justify-center gap-2">
                {forms.slice(0, cantidad).map((_, indice) => (
                  <button
                    key={indice}
                    type="button"
                    aria-label={`Ítem ${indice + 1}`}
                    aria-current={indice === slide}
                    disabled={formDisabled}
                    onClick={() => setSlide(indice)}
                    className={`h-2 rounded-full transition-[width,background-color] duration-[220ms] ease-out ${
                      indice === slide
                        ? "w-5 bg-foreground"
                        : "w-2 bg-muted-foreground/40"
                    }`}
                  />
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            {([1, 2, 3, 4] as const).map((n) => (
              <Button
                key={n}
                type="button"
                size="sm"
                variant={cantidad === n ? "secondary" : "outline"}
                disabled={formDisabled}
                aria-pressed={cantidad === n}
                aria-label={n === 1 ? "Un ítem" : `Unificar ${n} ítems`}
                onClick={() => elegirCantidad(n)}
              >
                {n}
              </Button>
            ))}
          </div>
          {distintos ? (
            <p className="text-sm text-destructive">
              Hay proveedores distintos. El producto toma el del primer ítem y
              no se puede guardar.
            </p>
          ) : null}
          <ConfiguradorProductoFormBody
            form={form}
            formDisabled={formDisabled}
            parte="comercial"
            ocultarObservacion={cantidad > 1}
          />
          {cantidad > 1 ? (
            <>
              <div>
                <label className={labelClass} htmlFor="cfg-observacion">
                  Observación fina (descripción){" "}
                  <span className="text-destructive">*</span>
                </label>
                <textarea
                  id="cfg-observacion"
                  data-config-field={KEY_OBS}
                  className={`${inputClass} min-h-[72px] resize-y`}
                  value={observacion}
                  disabled={formDisabled}
                  onChange={(e) => setComercial(KEY_OBS, e.target.value)}
                  placeholder="Descripción obligatoria del ítem"
                />
              </div>
              <div className="rounded-lg border border-border bg-muted/40 px-3 py-2">
                <p className="text-xs font-medium text-muted-foreground mb-1">
                  Vista previa descripción
                </p>
                <p className="text-sm break-words">{nombreUnificado()}</p>
              </div>
            </>
          ) : null}

          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              disabled={submitting}
              onClick={() => {
                onOpenChange(false);
                resetForm();
              }}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={
                busy ||
                evaluating ||
                distintos ||
                !fabricaValue ||
                !arbol ||
                (arbol.campos || []).length === 0
              }
              onClick={handleSubmit}
              className="bg-gradient-to-br from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creando…
                </>
              ) : (
                confirmLabel ||
                (isQuote ? "Agregar al presupuesto" : "Crear producto")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={avisoProveedor} onOpenChange={setAvisoProveedor}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Proveedores distintos</AlertDialogTitle>
            <AlertDialogDescription>
              El producto unificado toma la fábrica o el proveedor del primer
              ítem. Hay otro ítem con un proveedor distinto. Verificá la
              selección: así no se guarda.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setAvisoProveedor(false)}>
              Verificar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={formVisible.showNewProveedor && canCreateProveedor}
        onOpenChange={formVisible.setShowNewProveedor}
      >
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Crear nuevo proveedor</DialogTitle>
            <DialogDescription>
              Alta controlada para otras fábricas. Luego podrás seleccionarlo en
              el configurador.
            </DialogDescription>
          </DialogHeader>
          <ProveedoresABM
            createOnly
            onProveedorSaved={(codigo) => {
              proveedoresAPI.lookup({ activo: true }).then(async (list) => {
                formVisible.setProveedores(list);
                formVisible.setShowNewProveedor(false);
                if (codigo) {
                  const found = list.find((p) => p.codigo === codigo);
                  if (found?.id) {
                    await formVisible.setProveedorOtras(found.id);
                    toast.success(
                      `Proveedor ${codigo} creado y seleccionado.`,
                    );
                    return;
                  }
                }
                toast.success("Proveedor creado.");
              });
            }}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={Boolean(conflict)}
        onOpenChange={(v) => {
          if (!v) setConflict(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Configuración ya existente</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  Ya existe un producto con la misma identidad técnica. No se
                  puede crear un duplicado (aunque el precio difiera).
                </p>
                {conflict && (
                  <div className="rounded-md border border-border bg-muted/50 px-3 py-2 text-foreground space-y-1">
                    <p>
                      <span className="font-medium">Código:</span>{" "}
                      {conflict.existente.codigo}
                    </p>
                    <p>
                      <span className="font-medium">Nombre:</span>{" "}
                      {conflict.existente.nombre}
                    </p>
                    <p>
                      <span className="font-medium">Precio vigente:</span> $
                      {formatMoney(
                        conflict.existente.precioFinal ??
                          conflict.existente.precioBase,
                      )}
                    </p>
                  </div>
                )}
                <p>
                  Gestioná el producto desde Stock o abrilo para revisarlo.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setConflict(null);
              }}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleOpenExistente}>
              {existenteActionLabel ||
                (onOpenExistente
                  ? "Abrir producto existente"
                  : "Ver código")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
